import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { listSaved, type StoryCard } from '../api/discover';
import { StoryCardView } from '../components/discover/StoryCardView';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, EmptyState, ErrorState, LoadingState } from '../components/ui';
import './SavedPage.css';

type State = 'loading' | 'ready' | 'error';

/**
 * `/saved` — the stories you kept.
 *
 * Private, and said so: nothing here is shown to the people who wrote them.
 * When something you kept is no longer readable the list says how many rather
 * than quietly getting shorter — a list that loses entries without a word is a
 * list nobody trusts.
 */
export function SavedPage() {
  const [state, setState] = useState<State>('loading');
  const [cards, setCards] = useState<StoryCard[]>([]);
  const [unavailable, setUnavailable] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const saved = await listSaved();
      setCards(saved.results);
      setUnavailable(saved.unavailable);
      setPage(saved.page);
      setPages(saved.total_pages);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const saved = await listSaved(page + 1);
      setCards((current) => [...current, ...saved.results]);
      setPage(saved.page);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setLoadingMore(false);
    }
  };

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Saved</h1>
        <p>Stories you kept to come back to. Only you can see this list.</p>
      </div>
    </div>
  );

  if (state === 'loading') {
    return (
      <AppShell header={header}>
        <LoadingState label="Looking…" />
      </AppShell>
    );
  }

  if (state === 'error') {
    return (
      <AppShell header={header}>
        <ErrorState
          title="We could not load your saved stories"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell header={header}>
      {unavailable > 0 && (
        <Alert tone="info" title="Some of what you kept is not available">
          {unavailable} {unavailable === 1 ? 'story is' : 'stories are'} no longer readable — taken
          down, or made private. They stay on your list in case they come back.
        </Alert>
      )}

      {cards.length === 0 ? (
        <EmptyState
          title="Nothing kept yet"
          description="When you find a story worth coming back to, keep it and it will be here."
        />
      ) : (
        <div className="sc-saved">
          {cards.map((card) => (
            <StoryCardView key={card.id} card={card} />
          ))}
        </div>
      )}

      {page < pages && (
        <div className="sc-saved__more">
          <Button variant="secondary" isLoading={loadingMore} onClick={() => void more()}>
            More
          </Button>
        </div>
      )}
    </AppShell>
  );
}
